import { useCallback, useRef, useState, useEffect } from 'react';
import { GoogleMap, useLoadScript, MarkerF } from '@react-google-maps/api';
import { apiKey } from './../firebaseConfig';

const GOOGLE_MAPS_LIBRARIES = ['places', 'geocoding'];

const mapContainerStyle = {
  width: '100%',
  height: '300px'
};

function calculateInitialRegion(locations) {
  if (!locations || locations.length === 0) {
    return {
      center: { lat: 47.6062, lng: -122.3321 }, // Default to Seattle
      zoom: 8
    };
  }

  // Calculate center
  let totalLat = 0;
  let totalLng = 0;
  let validLocations = 0;

  for (const location of locations) {
    const lat = parseFloat(location.latitude);
    const lng = parseFloat(location.longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      totalLat += lat;
      totalLng += lng;
      validLocations++;
    }
  }

  if (validLocations === 0) {
    return {
      center: { lat: 47.6062, lng: -122.3321 }, // Default to Seattle
      zoom: 8
    };
  }

  const centerLat = totalLat / validLocations;
  const centerLng = totalLng / validLocations;

  // Calculate span
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  for (const location of locations) {
    const lat = parseFloat(location.latitude);
    const lng = parseFloat(location.longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
    }
  }

  const latDelta = maxLat - minLat;
  const lngDelta = maxLng - minLng;

  return {
    center: { lat: centerLat, lng: centerLng },
    zoom: Math.min(15, Math.max(8, Math.floor(14 - Math.log2(Math.max(latDelta, lngDelta)))))
  };
}
/*
const wheatMarkerIcon = {
  path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
  fillColor: "#F5DEB3", // wheat color
  fillOpacity: 1,
  strokeWeight: 1,
  strokeColor: "#000000",
  scale: 1.5,
};
*/
function QuestMap({ steps, currentStep, onMarkerClick, isReadOnly = false }) {
  const mapRef = useRef(null);
  const [locations, setLocations] = useState(null);
  const [center, setCenter] = useState(null);
  const [zoom, setZoom] = useState(12);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  useEffect(() => {
    if (steps) {
      const stepsWithLocations = steps.filter((step) => {
        const lat = parseFloat(step.latitude);
        const lng = parseFloat(step.longitude);
        return !isNaN(lat) && !isNaN(lng);
      });

      setLocations(stepsWithLocations.length > 0 ? stepsWithLocations : null);
      const region = calculateInitialRegion(stepsWithLocations, 0.1);
      setCenter(region.center);
      setZoom(region.zoom);
    }
  }, [steps]);

  const recenterMap = useCallback((newCoordinate) => {
    if (newCoordinate) {
      const lat = parseFloat(newCoordinate.latitude);
      const lng = parseFloat(newCoordinate.longitude);

      if (!isNaN(lat) && !isNaN(lng)) {
        const center = { lat, lng };
        setCenter(center);
        setZoom(12);

        if (mapRef.current) {
          mapRef.current.panTo(center);
        }
      }
    }
  }, []);

  // Add effect to recenter map when currentStep changes
  useEffect(() => {
    if (currentStep) {
      recenterMap(currentStep);
    }
  }, [currentStep, recenterMap]);

  if (!locations || !isLoaded) {
    return null;
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={center}
      zoom={zoom}
      onLoad={onLoad}
      options={{
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false
      }}
    >
      {locations.map((location) => {
        const lat = parseFloat(location.latitude);
        const lng = parseFloat(location.longitude);

        if (isNaN(lat) || isNaN(lng)) {
          return null;
        }

        return (
          <MarkerF
            key={location.id}
            position={{ lat, lng }}
            title={location.name}
            onClick={() => {
              if (onMarkerClick) {
                onMarkerClick(location);
              }
              if (!isReadOnly) {
                recenterMap(location);
              }
            }}
          />
        );
      })}
    </GoogleMap>
  );
}

export default QuestMap;
